import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../config";

function ManualInvoice() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    clientId: "",
    dueDate: "",
    paymentMethod: "UPI",
    currency: "INR",
    sendNow: true,
    addGstNumber: false,
    taxName: "",
    taxAmount: "",
    items: [{ name: "", amount: "" }],
  });

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getClientData`)
      .then((res) => setClients(Array.isArray(res.data) ? res.data : []))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!form.clientId) {
      setClientProjects([]);
      setSelectedProjectIds([]);
      return;
    }
    axios
      .get(`${API_URL}/api/getProjectbyClient/${form.clientId}`)
      .then((res) => setClientProjects(Array.isArray(res.data) ? res.data : []))
      .catch(() => setClientProjects([]));
  }, [form.clientId]);

  const selectedClient = useMemo(
    () => clients.find((c) => String(c._id) === String(form.clientId)),
    [clients, form.clientId]
  );

  const total = useMemo(
    () =>
      form.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [form.items]
  );

  const currSym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[form.currency] || "₹";

  const onChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const checkboxStyle = (checked) => ({
    width: 18,
    height: 18,
    border: "1px solid #6b7280",
    borderRadius: 4,
    backgroundColor: "#fff",
    appearance: "none",
    WebkitAppearance: "none",
    cursor: "pointer",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "12px 12px",
    backgroundImage: checked
      ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3 8.5l3 3 7-7' fill='none' stroke='%232563eb' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
      : "none",
  });

  const updateItem = (idx, key, value) => {
    setForm((prev) => {
      const next = [...prev.items];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, items: next };
    });
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { name: "", amount: "" }],
    }));
  };

  const removeItem = (idx) => {
    setForm((prev) => {
      if (prev.items.length === 1) return prev;
      return { ...prev, items: prev.items.filter((_, i) => i !== idx) };
    });
  };

  const getProjectName = (p) =>
    p?.projectName || p?.name || p?.title || p?.project_type || "Unnamed Project";

  const getProjectAmount = (p) => Number(p?.project_price || p?.price || p?.amount || 0);

  const toggleProject = (project) => {
    const pid = String(project._id);
    const alreadySelected = selectedProjectIds.includes(pid);

    if (alreadySelected) {
      setSelectedProjectIds((prev) => prev.filter((id) => id !== pid));
      setForm((prev) => ({
        ...prev,
        items: prev.items.filter((it) => String(it.projectId || "") !== pid),
      }));
      return;
    }

    setSelectedProjectIds((prev) => [...prev, pid]);
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          projectId: pid,
          name: getProjectName(project),
          amount: getProjectAmount(project),
        },
      ],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedClient) return alert("Please select a client.");
    if (!selectedClient.emailId) return alert("Selected client has no email.");
    if (!form.dueDate) return alert("Please select due date.");

    const cleanItems = form.items
      .map((it) => ({
        name: String(it.name || "").trim(),
        amount: Number(it.amount || 0),
      }))
      .filter((it) => it.name && it.amount > 0);

    if (!cleanItems.length) {
      return alert("Add at least one valid line item.");
    }

    try {
      setLoading(true);
      const payload = {
        clientId: selectedClient._id,
        clientName: selectedClient.leadName || "",
        clientEmail: selectedClient.emailId || "",
        clientGstNumber: form.addGstNumber ? (selectedClient.gstNumber || "") : "",
        clientAccountNo: form.addGstNumber ? (selectedClient.accountNo || "") : "",
        taxName: form.addGstNumber ? String(form.taxName || "").trim() : "",
        taxAmount: form.addGstNumber ? Number(form.taxAmount || 0) : 0,
        dueDate: form.dueDate,
        paymentMethod: form.paymentMethod,
        currency: form.currency,
        sendNow: form.sendNow,
        projects: cleanItems.map((it) => ({
          projectName: it.name,
          name: it.name,
          amount: it.amount,
        })),
      };

      const res = await axios.post(`${API_URL}/api/createInvoice`, payload);
      const created = res.data?.invoice || res.data;
      if (created?._id) {
        alert("Invoice created successfully.");
        navigate(`/admin/viewInvoice/${created._id}`);
      } else {
        alert("Invoice created, but could not open detail.");
      }
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to create invoice.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 900 }}>
      <div className="mb-3">
        <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
      <h3 className="mb-3">Add Invoice</h3>
      <form onSubmit={handleSubmit} className="card p-3 shadow-sm">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Client *</label>
            <select
              className="form-select"
              value={form.clientId}
              onChange={(e) => onChange("clientId", e.target.value)}
              required
            >
              <option value="">Select Client</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>
                  {(c.leadName || "Client")} ({c.leadId || c._id})
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Due Date *</label>
            <input
              type="date"
              className="form-control"
              value={form.dueDate}
              onChange={(e) => onChange("dueDate", e.target.value)}
              required
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Payment Method</label>
            <select
              className="form-select"
              value={form.paymentMethod}
              onChange={(e) => onChange("paymentMethod", e.target.value)}
            >
              <option value="UPI">UPI</option>
              <option value="Net Banking">Net Banking</option>
              <option value="Debit Card">Debit Card</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Cash">Cash</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Currency</label>
            <select
              className="form-select"
              value={form.currency}
              onChange={(e) => onChange("currency", e.target.value)}
            >
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD (CA$)</option>
            </select>
          </div>
        </div>

        <div className="form-check mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="addGstNumber"
            checked={form.addGstNumber}
            onChange={(e) => onChange("addGstNumber", e.target.checked)}
            style={checkboxStyle(form.addGstNumber)}
          />
          <label className="form-check-label" htmlFor="addGstNumber">
            Add GST Number
          </label>
        </div>

        {form.addGstNumber && (
          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <label className="form-label">GST Number</label>
              <input
                type="text"
                className="form-control"
                value={selectedClient?.gstNumber || ""}
                readOnly
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Account Number</label>
              <input
                type="text"
                className="form-control"
                value={selectedClient?.accountNo || ""}
                readOnly
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Tax Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. GST 18%"
                value={form.taxName}
                onChange={(e) => onChange("taxName", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Tax Amount</label>
              <input
                type="number"
                className="form-control"
                min="0"
                placeholder="Enter tax amount"
                value={form.taxAmount}
                onChange={(e) => onChange("taxAmount", e.target.value)}
              />
            </div>
          </div>
        )}

        {form.clientId && (
          <div className="mt-3">
            <label className="form-label fw-semibold">Client Projects</label>
            {clientProjects.length === 0 ? (
              <div className="text-muted small">No projects found for this client.</div>
            ) : (
              <div className="row g-2">
                {clientProjects.map((p) => {
                  const pid = String(p._id);
                  return (
                    <div className="col-md-6" key={pid}>
                      <div className="border rounded p-2 d-flex align-items-center">
                        <input
                          className="me-2"
                          type="checkbox"
                          id={`proj-${pid}`}
                          checked={selectedProjectIds.includes(pid)}
                          onChange={() => toggleProject(p)}
                          style={{ ...checkboxStyle(selectedProjectIds.includes(pid)), marginTop: 0, flex: "0 0 auto" }}
                        />
                        <label className="mb-0" htmlFor={`proj-${pid}`}>
                          {getProjectName(p)} ({currSym}{getProjectAmount(p).toLocaleString()})
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <hr />
        <h5 className="mb-3">Line Items</h5>
        {form.items.map((item, idx) => (
          <div className="row g-2 mb-2" key={idx}>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control"
                placeholder="Description"
                value={item.name}
                onChange={(e) => updateItem(idx, "name", e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <input
                type="number"
                className="form-control"
                placeholder="Amount"
                min="0"
                value={item.amount}
                onChange={(e) => updateItem(idx, "amount", e.target.value)}
              />
            </div>
            <div className="col-md-2 d-grid">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => removeItem(idx)}
                disabled={form.items.length === 1}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <div className="mb-3">
          <button type="button" className="btn btn-outline-primary" onClick={addItem}>
            + Add Item
          </button>
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <strong>Total: {currSym}{total.toLocaleString()}</strong>
          <button className="btn btn-success" type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ManualInvoice;
